import React, { type FC } from 'react'

import { useIntl } from 'react-intl'

import styled from '@emotion/styled'

import {
  BUTTON_VARIANT_PRIMARY,
  Button,
  Flex,
  FlexItem,
  MODAL_SIZE_LG,
  Modal,
  ModalBody,
  TEXT_VARIANT_ITALIC_LIGHT,
  TITLE_VARIANT_MODAL,
  Text,
  Title,
} from 'mailup-web-components'

import IconDataSyncPromo from 'Components/icons/IconDataSyncPromo'

import { isEcommerceVisible } from 'Helpers/ecommerce'

const StyledDescription = styled.div`
  ul {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 16px;

    padding-left: 24px;

    li {
      list-style-type: disc;
      list-style-position: outside;
    }
  }
`

const StyledPrice = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;

  span:nth-of-type(1) {
    font-weight: 500;
    padding-right: 0.25rem;
  }

  span:nth-of-type(2) {
    font-weight: 500;
    font-size: 22px;
  }

  span:nth-of-type(3) {
    font-weight: 500;
  }

  span:nth-of-type(4) {
    padding-left: 0.25rem;
    color: #7c7c7c;
  }
`

const StyledContacts = styled(Text)`
  font-size: 14px !important;
`

interface ModalDataSyncPromoProps {
  show: boolean
  onCancel: () => void
  onConfirm: () => void
}

const ModalDataSyncPromo: FC<ModalDataSyncPromoProps> = ({
  show,
  onCancel,
  onConfirm,
}) => {
  const intl = useIntl()

  const hasMshop: boolean = isEcommerceVisible()

  return (
    <Modal
      show={show}
      size={MODAL_SIZE_LG}
      data-mu-test="modal-data-sync-promo"
      onClose={onCancel}
      closeOnOverlayClick
      showClose
    >
      <ModalBody>
        <Flex gap="30px" margin="20px 0 0 0">
          <FlexItem flex="265px 0 0">
            <IconDataSyncPromo />
          </FlexItem>

          <FlexItem flexGrow={1}>
            <Flex flexDirection="column" gap="24px">
              <Title variant={TITLE_VARIANT_MODAL}>
                {intl.formatMessage({
                  id: 'Load.DataSyncPromo.ModalDataSyncTitle',
                })}
              </Title>

              <StyledDescription>
                {intl.formatMessage(
                  {
                    id: 'Load.DataSyncPromo.ModalDataSyncDescription',
                  },
                  {
                    b: (chunks) => <strong>{chunks}</strong>,
                    ul: (chunks) => <ul>{chunks}</ul>,
                    li: (chunks) => <li>{chunks}</li>,
                  },
                )}
              </StyledDescription>

              <Flex flexDirection="column" gap="4px">
                <Flex alignItems="center" justifyContent="space-between">
                  <StyledPrice>
                    {intl.formatMessage(
                      { id: 'Load.DataSyncPromo.Price' },
                      { span: (chunks) => <span>{chunks}</span> },
                    )}
                  </StyledPrice>
                  {hasMshop && (
                    <Button
                      data-mu-test="modal-data-sync-promo__confirm"
                      kind={BUTTON_VARIANT_PRIMARY}
                      onClick={onConfirm}
                    >
                      {intl.formatMessage({
                        id: 'Load.DataSyncPromo.ModalConfirm',
                      })}
                    </Button>
                  )}
                </Flex>
                {!hasMshop && (
                  <StyledContacts variant={TEXT_VARIANT_ITALIC_LIGHT}>
                    {intl.formatMessage({
                      id: 'Load.DataSyncPromo.ContactAdmin',
                    })}
                  </StyledContacts>
                )}
              </Flex>
            </Flex>
          </FlexItem>
        </Flex>
      </ModalBody>
    </Modal>
  )
}

export default ModalDataSyncPromo
